import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, FolderClosed, ArrowDown, Network } from "lucide-react";
import { CLEARANCE_TONE } from "./oracleClient";
import { desk } from "../member-desk/tokens";

const TIER_TONE = {
  documented: "#5E9E7A",
  role: "#C58A2E",
  context: "#5A554C",
};

const DataBlock = ({ data }) => {
  if (!data) return null;
  const rows = data.rows || [];
  const stat = data.stat || null;
  return (
    <div className="mt-4" data-testid="oracle-data">
      {stat && (
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(stat).map(([k, v]) =>
            v === null || v === undefined ? null : (
              <div key={k} className="border px-3 py-2" style={{ borderColor: desk.edge, background: "#12110E" }}>
                <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168]">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="font-display text-[20px] leading-none mt-1" style={{ color: desk.ink }}>{String(v)}</div>
              </div>
            ))}
        </div>
      )}
      {rows.length > 0 && (
        <ul className="border divide-y" style={{ borderColor: desk.edge, background: desk.surface }}>
          {rows.map((r, i) => {
            const label = r.title || r.source || r.action || r.callsign || r.reason || "—";
            const meta = r.status || r.score || r.severity || r.type || r.at || null;
            const to = r.slug ? (r.type === "profile" ? null : `/topic/${r.slug}`) : null;
            const inner = (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-[13px] text-[#C9C3B5] truncate">{label}</span>
                {meta && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#8A8377] shrink-0">
                    {String(meta)}
                  </span>
                )}
              </div>
            );
            return (
              <li key={i} data-testid="oracle-data-row">
                {to ? <Link to={to} className="block hover:bg-[#161418] transition-colors">{inner}</Link> : inner}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const SectionLabel = ({ icon: Icon, children, tone }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={11} style={{ color: tone }} />
    <span className="font-mono text-[9px] uppercase tracking-[0.35em]" style={{ color: tone }}>
      {children}
    </span>
  </div>
);

const ShowMore = ({ n, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-2 border transition-colors hover:text-[#E4DFD3]"
          style={{ borderColor: desk.edge, color: desk.inkDim }}>
    Show {n} more
  </button>
);

const EmptyLine = ({ children, testid }) => (
  <p className="mt-4 font-mono text-[10px] tracking-[0.05em]" style={{ color: desk.inkMuted }} data-testid={testid}>
    {children}
  </p>
);

const DossierCards = ({ cards, tone }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? cards : cards.slice(0, 2);
  return (
    <div className="mt-6" data-testid="oracle-dossier-cards">
      <SectionLabel icon={FolderClosed} tone={tone}>Case Files</SectionLabel>
      <div className="grid gap-3 sm:grid-cols-2">
        {visible.map((c) => (
          <div key={c.slug} className="border p-4 flex flex-col animate-in fade-in duration-300"
               style={{ borderColor: desk.edge, background: desk.surface }}
               data-testid="oracle-dossier-card">
            <div className="font-mono text-[8px] uppercase tracking-[0.3em]" style={{ color: tone }}>
              {c.role_label}
            </div>
            <div className="font-display text-[16px] leading-snug mt-1.5" style={{ color: desk.ink }}>{c.title}</div>
            {c.tagline && (
              <div className="text-[11px] leading-relaxed mt-1 line-clamp-2" style={{ color: desk.inkDim }}>{c.tagline}</div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              {c.evidence_score != null && (
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168]">
                  Evidence <span className="text-[#C9C3B5]">{c.evidence_score}/100</span>
                </span>
              )}
              {c.updated_at && (
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#7B7168]">
                  Updated <span className="text-[#C9C3B5]">{c.updated_at}</span>
                </span>
              )}
            </div>
            {c.relationship && (
              <div className="text-[10px] italic leading-relaxed mt-2 border-l pl-2"
                   style={{ borderColor: desk.edge, color: "#9C9587" }}>
                {c.relationship}
              </div>
            )}
            <Link to={`/topic/${c.slug}`} data-testid="oracle-open-dossier"
                  className="mt-3 inline-flex self-start items-center gap-1.5 border px-3 py-2.5 min-h-[44px] sm:min-h-0 font-mono text-[9px] uppercase tracking-[0.25em] text-[#C9C3B5] hover:text-[#E4DFD3] hover:border-[#A68F5E] transition-colors"
                  style={{ borderColor: desk.edgeHi }}>
              Open Dossier
            </Link>
          </div>
        ))}
      </div>
      {!expanded && cards.length > 2 && (
        <ShowMore n={cards.length - 2} onClick={() => setExpanded(true)} testid="oracle-cards-more" />
      )}
    </div>
  );
};

const RelatedIntel = ({ items, tone }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, 3);
  return (
    <div className="mt-6" data-testid="oracle-related-intel">
      <SectionLabel icon={Network} tone={tone}>Related Intelligence</SectionLabel>
      <ul className="border divide-y" style={{ borderColor: desk.edge, background: desk.surface }}>
        {visible.map((it, i) => {
          const inner = (
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className={`text-[13px] ${it.href ? "text-[#D5CFC2] underline decoration-[#454038] underline-offset-2" : "text-[#C9C3B5]"}`}>
                  {it.name}
                </span>
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] shrink-0"
                      style={{ color: TIER_TONE[it.tier] || desk.inkMuted }}>
                  {it.tier_label}
                </span>
              </div>
              {it.reason && (
                <div className="text-[11px] leading-relaxed mt-1" style={{ color: desk.inkDim }}>{it.reason}</div>
              )}
            </div>
          );
          return (
            <li key={i} className="animate-in fade-in duration-300" data-testid="oracle-related-item">
              {it.href ? <Link to={it.href} className="block hover:bg-[#161418] transition-colors">{inner}</Link> : inner}
            </li>
          );
        })}
      </ul>
      {!expanded && items.length > 3 && (
        <ShowMore n={items.length - 3} onClick={() => setExpanded(true)} testid="oracle-related-more" />
      )}
    </div>
  );
};

const InvestigationPath = ({ path, tone }) => (
  <div className="mt-6" data-testid="oracle-path">
    <SectionLabel icon={ArrowDown} tone={tone}>Recommended Investigation Path</SectionLabel>
    <ol>
      {path.map((s, i) => (
        <li key={i} data-testid="oracle-path-step">
          <div className="flex gap-3 border px-4 py-3"
               style={{ borderColor: desk.edge, background: desk.surface }}>
            <span className="font-display text-[18px] leading-none mt-0.5" style={{ color: tone }}>
              {i + 1}
            </span>
            <div className="min-w-0">
              {s.href ? (
                <Link to={s.href} className="text-[13px] text-[#D5CFC2] underline decoration-[#454038] underline-offset-2 hover:text-[#E4DFD3] transition-colors">
                  {s.title}
                </Link>
              ) : (
                <span className="text-[13px] text-[#C9C3B5]">{s.title}</span>
              )}
              {s.why && (
                <div className="text-[11px] leading-relaxed mt-0.5" style={{ color: desk.inkDim }}>{s.why}</div>
              )}
            </div>
          </div>
          {i < path.length - 1 && (
            <div className="flex justify-center py-1">
              <ArrowDown size={12} style={{ color: desk.edgeHi }} />
            </div>
          )}
        </li>
      ))}
    </ol>
  </div>
);

const OracleAnswerInner = ({ res }) => {
  const tone = CLEARANCE_TONE[res.header?.clearance] || "#A68F5E";
  const denied = res.authorized === false;
  const related = res.related || null;
  const isResearch = res.intent === "research" && !denied;
  const hasRelatedContext = !!(related?.items?.length || related?.cards?.length);
  return (
    <div className="border p-5 md:p-6 mb-4"
         style={{ borderColor: denied ? "#3A211F" : desk.edge, background: "#0C0B0E" }}
         data-testid="oracle-answer">
      <div className="flex items-center gap-2">
        {denied ? <Lock size={12} style={{ color: tone }} /> : <Sparkles size={12} style={{ color: tone }} />}
        <span className="font-mono text-[10px] uppercase tracking-[0.45em]" style={{ color: tone }}
              data-testid="oracle-clearance-label">
          {res.header?.clearance}
        </span>
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] mt-1" style={{ color: desk.inkMuted }}>
        {res.header?.subtitle}
      </div>
      <p className={`text-[14px] leading-relaxed mt-3 whitespace-pre-line ${denied ? "text-[#C99B96]" : "text-[#D5CFC2]"}`}
         data-testid={denied ? "oracle-denial" : "oracle-narrative"}>
        {res.narrative}
      </p>
      {!denied && related?.cards?.length > 0 && <DossierCards cards={related.cards} tone={tone} />}
      {!denied && !related && <DataBlock data={res.data} />}
      {!denied && related?.items?.length > 0 && <RelatedIntel items={related.items} tone={tone} />}
      {isResearch && !related?.items?.length && (
        <EmptyLine testid="oracle-no-related">
          No additional related intelligence is currently available.
        </EmptyLine>
      )}
      {!denied && related?.path?.length > 0 && <InvestigationPath path={related.path} tone={tone} />}
      {isResearch && hasRelatedContext && !related?.path?.length && (
        <EmptyLine testid="oracle-no-path">
          No defensible investigation path could be generated from current Archive records.
        </EmptyLine>
      )}
    </div>
  );
};

export const OracleAnswer = React.memo(OracleAnswerInner);
